// The TMS coil's casing (ba-look's note design/art-direction/notes/2026-10-03-stim-coil-look.md; the lead, 02:39 UTC
// 10-03): a figure-eight shell in the coil's own frame, the frame ba-stim's dipoles and poses use (SimNIBS: x and y
// tangent to the contact face, z into the head, mm). Two discs of radius 45 mm at x = ±45 mm, joined at the waist by a
// neck, 12 mm thick from the contact face (z = 0) back (z = −12, where the dipoles sit 4.2–8.8 mm behind the face),
// and a 40 × 20 mm handle stub along +y, the side the pose's handle reference (CP3 for the C3 rule) lies on. One simple
// outline extruded, so the translucent shell never doubles up where pieces would overlap. Artist-made geometry from the
// note's numbers, not device CAD: the metadata says so.

/** Design's `devices.tms_coil` (design/tokens.json 0.1.12), copied so the app doesn't bundle the render tokens; the unit
 *  test holds these to the file. The casing's fill, fresnel edge and depth, and the Source model's dipoles. `opacity` is
 *  the look's composed value through a wing (the mock's single layer); `face_opacity` is what the mesh command takes,
 *  because the closed shell's caps and walls each draw at it with no depth write, about 2.3–2.8 faces at the hot spot.
 *  ba-look's measurements on PR #759's paired frames: per-face 0.18 with the ghost's rim at 0.55 composed to 0.37–0.43
 *  and left the hot spot at 63–65 % of its bare contrast against the rule's 80 %; per-face 0.06 passed from the left
 *  (83 %) and failed from above (69 %), where the near wing's rim crosses the hot spot and its fresnel adds a constant
 *  veil no opacity removes; so 0.04 per face with the rim's strength at 0.20, predicted at 83 % from above and over 90 %
 *  from the left. The walls are edge-on where the fresnel is at full value, so the silhouette still draws. */
export const COIL_LOOK = {
  casing: { color: '#C9CED6', opacity: 0.08, face_opacity: 0.04, specular: 0, depth_write: false, fresnel: { color: '#9AA6B8', power: 3, strength: 0.2 } },
  source_model: { glyph_radius_mm: 0.5, color: '#C9CED6', opacity: 0.6, casing_opacity_while_on: 0.04, face_opacity_while_on: 0.02 },
} as const;

export interface CasingMesh {
  positions: Float32Array;
  normals: Float32Array;
  indices: Uint32Array;
  metadata: { provenance: 'artist-made'; units: 'mm'; description: string; outline: number };
}

export const CASING = {
  /** Each wing's radius and the distance of its centre from the waist, mm. */
  radius: 45,
  centre: 45,
  /** Half the neck's height at the waist, mm: the join between the wings. */
  neck: 14,
  /** The shell's depth behind the contact face, mm. */
  depth: 12,
  /** The handle stub: its length along +y and its full width in x, mm. */
  handle: { length: 40, width: 20 },
  /** Points along each wing's full circle. */
  segments: 64,
} as const;

/** The casing's outline in the contact plane, counter-clockwise, as [x, y] pairs: the left wing's arc, the neck's
 *  underside, the right wing's arc, then the handle stub between the wings' tops. */
export function casingOutline(c: typeof CASING = CASING): [number, number][] {
  const { radius: r, centre: d, neck: b, segments } = c;
  const hw = c.handle.width / 2;
  const top = Math.sqrt(r * r - (d - hw) * (d - hw)); // the wings' height where the stub's sides meet them
  const phiB = Math.asin(b / r); // the neck's half-angle on a wing
  const phiS = Math.acos((d - hw) / r); // the stub side's angle on a wing
  const step = (2 * Math.PI) / segments;
  const out: [number, number][] = [];
  const arc = (cx: number, from: number, to: number) => {
    // From `from` to `to`, counter-clockwise, at most `step` apart, both ends included.
    const n = Math.max(1, Math.ceil((to - from) / step));
    for (let i = 0; i <= n; i++) {
      const a = from + ((to - from) * i) / n;
      out.push([cx + r * Math.cos(a), r * Math.sin(a)]);
    }
  };
  // The left wing, from the stub's left side over the top and round the far left to the neck's underside.
  arc(-d, phiS, 2 * Math.PI - phiB);
  // The right wing, from the neck's underside round the far right and over the top to the stub's right side.
  arc(d, Math.PI + phiB, 3 * Math.PI - phiS);
  // The stub: up its right side, across its end, down its left side to the left wing's start.
  const y1 = top + c.handle.length;
  out.push([hw, y1], [-hw, y1]);
  // Drop points closer than 1e-6 to their predecessor (the arcs' ends meet the stub's sides exactly).
  const clean: [number, number][] = [];
  for (const p of out) {
    const q = clean[clean.length - 1];
    if (!q || Math.hypot(p[0] - q[0], p[1] - q[1]) > 1e-6) clean.push(p);
  }
  const first = clean[0]!;
  const last = clean[clean.length - 1]!;
  if (Math.hypot(first[0] - last[0], first[1] - last[1]) <= 1e-6) clean.pop();
  return clean;
}

/** Twice the signed area: positive for a counter-clockwise ring. */
export const signedArea2 = (ring: [number, number][]): number => ring.reduce((a, p, i) => a + p[0] * ring[(i + 1) % ring.length]![1] - ring[(i + 1) % ring.length]![0] * p[1], 0);

/** Ear clipping of a simple counter-clockwise polygon: triangles as index triples into `ring`. */
export function triangulate(ring: [number, number][]): number[] {
  const n = ring.length;
  if (n < 3) return [];
  const idx = Array.from({ length: n }, (_, i) => i);
  if (signedArea2(ring) < 0) idx.reverse();
  const out: number[] = [];
  const cross = (o: [number, number], a: [number, number], b: [number, number]) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const inside = (p: [number, number], a: [number, number], b: [number, number], c: [number, number]) => cross(a, b, p) >= -1e-9 && cross(b, c, p) >= -1e-9 && cross(c, a, p) >= -1e-9;
  let guard = 0;
  while (idx.length > 3 && guard++ < 10_000) {
    let clipped = false;
    for (let i = 0; i < idx.length; i++) {
      const i0 = idx[(i + idx.length - 1) % idx.length]!;
      const i1 = idx[i]!;
      const i2 = idx[(i + 1) % idx.length]!;
      const a = ring[i0]!;
      const b = ring[i1]!;
      const c = ring[i2]!;
      if (cross(a, b, c) <= 1e-9) continue; // reflex or flat: not an ear
      let ear = true;
      for (const j of idx) {
        if (j === i0 || j === i1 || j === i2) continue;
        if (inside(ring[j]!, a, b, c)) {
          ear = false;
          break;
        }
      }
      if (!ear) continue;
      out.push(i0, i1, i2);
      idx.splice(i, 1);
      clipped = true;
      break;
    }
    if (!clipped) break; // degenerate input: stop rather than spin
  }
  if (idx.length === 3) out.push(idx[0]!, idx[1]!, idx[2]!);
  return out;
}

/** The casing as a mesh: the outline extruded from the contact face (z = 0) back to z = −depth, flat caps and flat
 *  sides (each side quad has its own four vertices, so its normal is its own). */
export function createCasingMesh(c: typeof CASING = CASING): CasingMesh {
  const ring = casingOutline(c);
  const n = ring.length;
  const tris = triangulate(ring);
  const z0 = 0;
  const z1 = -c.depth;
  // Vertices: the face cap (n), the back cap (n), then four per side quad (4n).
  const positions = new Float32Array((2 * n + 4 * n) * 3);
  const normals = new Float32Array(positions.length);
  const put = (v: number, x: number, y: number, z: number, nx: number, ny: number, nz: number) => {
    positions[v * 3] = x;
    positions[v * 3 + 1] = y;
    positions[v * 3 + 2] = z;
    normals[v * 3] = nx;
    normals[v * 3 + 1] = ny;
    normals[v * 3 + 2] = nz;
  };
  for (let i = 0; i < n; i++) {
    const [x, y] = ring[i]!;
    put(i, x, y, z0, 0, 0, 1); // the contact face looks toward the head (+z)
    put(n + i, x, y, z1, 0, 0, -1); // the back looks away from it
  }
  const indices: number[] = [];
  // The face cap keeps the ring's winding (counter-clockwise seen from +z); the back cap is reversed.
  for (let t = 0; t < tris.length; t += 3) {
    indices.push(tris[t]!, tris[t + 1]!, tris[t + 2]!);
    indices.push(n + tris[t]!, n + tris[t + 2]!, n + tris[t + 1]!);
  }
  for (let i = 0; i < n; i++) {
    const [ax, ay] = ring[i]!;
    const [bx, by] = ring[(i + 1) % n]!;
    // The outward normal of a counter-clockwise ring's edge: the edge turned clockwise.
    const ex = bx - ax;
    const ey = by - ay;
    const len = Math.hypot(ex, ey) || 1;
    const nx = ey / len;
    const ny = -ex / len;
    const v = 2 * n + 4 * i;
    put(v, ax, ay, z0, nx, ny, 0);
    put(v + 1, bx, by, z0, nx, ny, 0);
    put(v + 2, bx, by, z1, nx, ny, 0);
    put(v + 3, ax, ay, z1, nx, ny, 0);
    // Two triangles facing outward (counter-clockwise seen from outside).
    indices.push(v, v + 3, v + 2, v, v + 2, v + 1);
  }
  return {
    positions,
    normals,
    indices: Uint32Array.from(indices),
    metadata: {
      provenance: 'artist-made',
      units: 'mm',
      description: `A figure-eight casing from ba-look's numbers (two ${c.radius} mm discs at x = ±${c.centre} mm, ${c.depth} mm deep, a ${c.handle.length} × ${c.handle.width} mm handle stub toward +y), not device geometry.`,
      outline: n,
    },
  };
}
